import React, { useMemo } from 'react';

import { AgGridReact } from 'ag-grid-react';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import { accountStatusOptions } from '@/constants';
import { DonorSchedule } from '@/types.gql';

import styles from './EditButton.module.css';

export const EditButton = ({
  gridRef,
}: {
  gridRef: React.RefObject<AgGridReact<DonorSchedule>>;
}) => {
  const [params, setParams] = useSearchParams();
  const { t } = useTranslation();
  const { selectedDonorSchedules } = useAppSelector(
    (state) => state.donorScheduling,
  );
  const { workingDonorSchedule } = useAppSelector(
    (state) => state.donorScheduling,
  );

  const closedNoNewBusinessValue =
    accountStatusOptions.find((opt) => opt.label === 'Closed - No New Business')
      ?.value ??
    accountStatusOptions.find((opt) => opt.value === 100000003)?.value ??
    null;

  const selectedCount = gridRef?.current?.api?.getSelectedNodes().length ?? 0;

  const isPastDue = useMemo(() => {
    return !!(
      workingDonorSchedule?.owner?.aging180 ||
      workingDonorSchedule?.owner?.aging365 ||
      workingDonorSchedule?.owner?.accountStatus === closedNoNewBusinessValue
    );
  }, [
    workingDonorSchedule?.owner?.aging180,
    workingDonorSchedule?.owner?.aging365,
    workingDonorSchedule?.owner?.accountStatus,
  ]);

  const label = useMemo(() => {
    if (selectedCount > 0 && isPastDue) {
      return t('common.viewSelected');
    }

    if (selectedCount > 0) {
      return t('common.editSelected');
    }

    return t('donorScheduling.newDonorSchedule');
  }, [selectedCount, isPastDue, t]);

  const onClick = () => {
    const newParams = new URLSearchParams(params);
    newParams.set('edit', 'true');
    newParams.set('id', workingDonorSchedule.id ? workingDonorSchedule.id : -1);
    setParams(newParams);
  };

  return (
    <Button
      className={styles['edit-button']}
      label={label}
      icon={`pi pi-${isPastDue ? 'eye' : selectedCount > 0 ? 'pencil' : 'plus'}`}
      badge={selectedCount > 1 ? selectedCount.toString() : undefined}
      badgeClassName="p-badge-danger"
      onClick={onClick}
    />
  );
};
